import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PackCheckLogo } from './PackCheckLogo';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { Sun, Moon, LogOut, User as UserIcon, ShieldCheck, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import api from '../lib/api';
import { toast } from 'sonner';

const NavLink = ({ to, children, testid, onClick }) => {
  const loc = useLocation();
  const active = loc.pathname === to || (to !== '/' && loc.pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-2 text-sm transition-colors hover:text-foreground ${active ? 'text-foreground' : 'text-muted-foreground'}`}
    >
      {children}
    </Link>
  );
};

export const TopNav = () => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [googleConfig, setGoogleConfig] = useState({ configured: false, client_id: '' });
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    api.get('/auth/google/config').then(({ data }) => setGoogleConfig(data)).catch(() => {});
  }, []);

  // Close the drawer whenever the route changes
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (!googleConfig.configured || !googleConfig.client_id) {
      toast.error('Google sign-in is not configured yet. Ask your admin to set GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET, or use /admin/login with the admin password.');
      return;
    }
    const redirectUri = window.location.origin + '/auth/google';
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('pc_oauth_state', state);
    const params = new URLSearchParams({
      client_id: googleConfig.client_id,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      state,
      access_type: 'online',
      prompt: 'select_account',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const navItems = [
    user?.role !== 'super_admin' && { to: '/', label: 'Home', testid: 'nav-home' },
    user?.role !== 'super_admin' && { to: '/audit/new', label: 'Check a pack', testid: 'nav-new-audit' },
    user?.role !== 'super_admin' && { to: '/pricing', label: 'Pricing', testid: 'nav-pricing' },
    user?.role !== 'super_admin' && { to: '/updates', label: 'Updates', testid: 'nav-updates' },
    user && user.role !== 'super_admin' && { to: '/dashboard', label: 'Dashboard', testid: 'nav-dashboard' },
    (user?.role === 'admin' || user?.role === 'super_admin') && { to: '/admin', label: 'Admin', testid: 'nav-admin' },
    user && { to: '/account', label: 'Account', testid: 'nav-account' },
  ].filter(Boolean);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur" data-testid="top-nav">
      <div className="mx-auto max-w-[1400px] flex items-center justify-between px-4 sm:px-6 md:px-10 h-16 lg:h-24">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-home-link">
          <PackCheckLogo height={32} className="lg:hidden" />
          <PackCheckLogo height={52} className="hidden lg:block" />
        </Link>

        {/* Desktop nav (≥ lg) */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} testid={n.testid}>{n.label}</NavLink>
          ))}
        </nav>

        {/* Right cluster — theme toggle + auth + hamburger */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="h-9 w-9 grid place-items-center border border-border hover:bg-surface-hover transition-colors"
            title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          >
            {theme === 'dark' ? <Sun size={15} strokeWidth={1.5} /> : <Moon size={15} strokeWidth={1.5} />}
          </button>

          {/* Desktop auth state */}
          {user ? (
            <div className="hidden lg:flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 h-9 border border-border" data-testid="user-pill">
                {(user.role === 'admin' || user.role === 'super_admin') ? <ShieldCheck size={14} strokeWidth={1.5} className="text-accent" /> : <UserIcon size={14} strokeWidth={1.5} />}
                <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  {user.role === 'super_admin' ? 'Super-admin' : user.role === 'admin' ? 'Admin' : user.plan}
                </span>
                <span className="text-sm">{user.name?.split(' ')[0] || user.email}</span>
              </div>
              <button
                onClick={logout}
                data-testid="logout-btn"
                className="h-9 px-3 border border-border hover:bg-surface-hover transition-colors flex items-center gap-1 text-sm"
              >
                <LogOut size={13} strokeWidth={1.5} /> Sign out
              </button>
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-2">
              <Link
                to="/admin/login"
                data-testid="admin-login-link"
                className="inline-flex h-9 px-3 items-center border border-border hover:bg-surface-hover transition-colors text-xs font-mono uppercase tracking-widest text-muted-foreground"
                title="Sign in with admin password"
              >
                Admin
              </Link>
              <Button onClick={startLogin} data-testid="login-btn" className="h-9 rounded-none">
                Sign in
              </Button>
            </div>
          )}

          {/* Mobile/tablet hamburger (< lg) */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                className="lg:hidden h-9 w-9 grid place-items-center border border-border hover:bg-surface-hover transition-colors"
                aria-label="Open menu"
                data-testid="hamburger-btn"
              >
                {mobileOpen ? <X size={16} strokeWidth={1.5} /> : <Menu size={16} strokeWidth={1.5} />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="rounded-none w-[85vw] sm:w-[380px] p-0 flex flex-col" data-testid="mobile-drawer">
              <div className="flex items-center justify-between px-6 h-16 border-b border-border">
                <PackCheckLogo height={28} />
                <button
                  onClick={() => setMobileOpen(false)}
                  className="h-8 w-8 grid place-items-center border border-border hover:bg-surface-hover"
                  aria-label="Close menu"
                  data-testid="hamburger-close"
                >
                  <X size={15} strokeWidth={1.5} />
                </button>
              </div>

              {/* User pill on mobile */}
              {user && (
                <div className="px-6 py-4 border-b border-border">
                  <div className="flex items-center gap-2" data-testid="mobile-user-pill">
                    {(user.role === 'admin' || user.role === 'super_admin') ? <ShieldCheck size={14} strokeWidth={1.5} className="text-accent" /> : <UserIcon size={14} strokeWidth={1.5} />}
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {user.role === 'super_admin' ? 'Super-admin' : user.role === 'admin' ? 'Admin' : user.plan}
                    </span>
                  </div>
                  <div className="mt-1 text-sm font-medium truncate">{user.name || user.email}</div>
                  {user.name && <div className="text-xs text-muted-foreground truncate">{user.email}</div>}
                </div>
              )}

              <nav className="flex-1 overflow-y-auto py-4">
                {navItems.map((n) => {
                  const active = loc.pathname === n.to || (n.to !== '/' && loc.pathname.startsWith(n.to));
                  return (
                    <Link
                      key={n.to}
                      to={n.to}
                      data-testid={`mobile-${n.testid}`}
                      onClick={() => setMobileOpen(false)}
                      className={`block px-6 py-3 text-base transition-colors border-l-2 ${active ? 'border-foreground text-foreground bg-surface' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-surface-hover'}`}
                    >
                      {n.label}
                    </Link>
                  );
                })}
              </nav>

              <div className="border-t border-border p-6 space-y-3">
                {user ? (
                  <Button
                    onClick={() => { setMobileOpen(false); logout(); }}
                    variant="outline"
                    className="rounded-none w-full"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut size={14} strokeWidth={1.5} /> Sign out
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => { setMobileOpen(false); startLogin(); }}
                      className="rounded-none w-full"
                      data-testid="mobile-login-btn"
                    >
                      Sign in with Google
                    </Button>
                    <Link
                      to="/admin/login"
                      onClick={() => setMobileOpen(false)}
                      data-testid="mobile-admin-login-link"
                      className="block text-center text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground border border-border h-10 leading-[2.5rem]"
                    >
                      Admin sign-in
                    </Link>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export const Footer = () => (
  <footer className="border-t border-border mt-16 md:mt-24" data-testid="footer">
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
      <div className="space-y-3">
        <PackCheckLogo height={36} className="md:hidden" />
        <PackCheckLogo height={44} className="hidden md:block" />
        <p className="text-xs text-muted-foreground">Your label. Audited before it prints.</p>
      </div>
      <div className="space-y-2">
        <div className="label-mono-sm">Product</div>
        <Link to="/audit/new" className="block text-sm text-muted-foreground hover:text-foreground">Check a pack</Link>
        <Link to="/pricing" className="block text-sm text-muted-foreground hover:text-foreground">Pricing</Link>
        <Link to="/updates" className="block text-sm text-muted-foreground hover:text-foreground">Regulatory updates</Link>
      </div>
      <div className="space-y-2">
        <div className="label-mono-sm">Legal</div>
        <Link to="/refund-policy" className="block text-sm text-muted-foreground hover:text-foreground">Refund policy</Link>
        <Link to="/terms" className="block text-sm text-muted-foreground hover:text-foreground">Terms</Link>
        <Link to="/privacy" className="block text-sm text-muted-foreground hover:text-foreground">Privacy</Link>
      </div>
      <div className="space-y-2">
        <div className="label-mono-sm">Sources</div>
        <a href="https://www.fssai.gov.in" target="_blank" rel="noreferrer" className="block text-sm text-muted-foreground hover:text-foreground">fssai.gov.in</a>
        <a href="https://consumeraffairs.gov.in/pages/legal-metrology-overview" target="_blank" rel="noreferrer" className="block text-sm text-muted-foreground hover:text-foreground">Legal Metrology</a>
      </div>
      <div className="space-y-2">
        <div className="label-mono-sm">Disclaimer</div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          PackCheck is an automated assistance tool. Findings are indicative only and not legal advice or compliance certification. Users must verify compliance independently before printing or distribution.
        </p>
      </div>
    </div>
    <div className="border-t border-border">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>© 2026 Therefore Design Pvt. Ltd.</span>
          <span className="opacity-50">|</span>
          <span>Packcheck FSSAI &amp; Legal Metrology Audit</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <a href="mailto:info@thereforedesign.co.in" className="hover:text-foreground" data-testid="footer-email">info@thereforedesign.co.in</a>
          <span className="opacity-50">|</span>
          <a href="https://www.thereforedesign.co.in" target="_blank" rel="noreferrer" className="hover:text-foreground" data-testid="footer-website">www.thereforedesign.co.in</a>
          <span className="opacity-50">|</span>
          <Link to="/pricing" className="hover:text-foreground" data-testid="footer-pricing">Pricing</Link>
        </div>
      </div>
    </div>
  </footer>
);

export const Layout = ({ children }) => (
  <div className="App min-h-screen">
    <TopNav />
    <main>{children}</main>
    <Footer />
  </div>
);
